import { registerNotificationSink } from '@smartsash/server-ops';
import { notify } from '@smartsash/ui';

import i18n from '@/i18n';

/**
 * Bridge `@smartsash/server-ops` notifications into the shared toast layer (HS-847,
 * Phase 1c; retargeted onto `@smartsash/ui` by HS-1170). server-ops renders nothing and
 * translates nothing — it emits a message KEY + level; the app owns translation and
 * display. Registered once at bootstrap, before any server-ops mutation can emit.
 *
 * This used to push into `useUIStore.addNotification`, a store no component ever
 * rendered — every success and failure toast was built and then discarded. `notify()`
 * is a module-level seam, so it works outside React and needs nothing but a mounted
 * `<NotificationProvider>` (see `ui/App/UiProviders.tsx`).
 *
 * `NotificationLevel` ('success' | 'error' | 'info') is a subset of the UI's
 * `NotificationMediaVariant`, so the level maps straight onto the leading icon.
 * The emitted keys are `serverOps.toast.*` (server-ops' `SUCCESS_KEYS`) and `errors.*`
 * (api-client's `ERROR_MESSAGE_KEYS`). Both live in admin's own `app` namespace — the
 * default — because the wording is app-specific; `apps/portal` owns its own `errors.*`
 * for the same reason. Until they existed, i18next missed and echoed the raw key into
 * the toast.
 */
export function bootstrapServerOpsNotifications(): void {
  registerNotificationSink((notification) => {
    const message = i18n.t(notification.messageKey);
    // server-ops derives `detail` from `error.message`, and a `NormalizedApiError`
    // built without a server detail falls back to its own message key as the
    // message. Only surface `detail` when it is genuinely server-provided text.
    const detail =
      notification.detail && notification.detail !== notification.messageKey
        ? notification.detail
        : undefined;

    notify({
      title: message,
      description: detail,
      media: { kind: 'icon', variant: notification.level },
      role: notification.level === 'error' ? 'alert' : 'status',
      duration: notification.ttlMs,
    });
  });
}
